import { addDays } from 'date-fns';

import {
  ProductStatusEnum,
  ProductTypeName,
  RelatedProductCategoryNameEnum,
  ShippingMethod,
  WarehouseShippingMethod,
} from '@electricjs/core_entity-client';

import { Country } from '@electricjs/arc';

import {
  AssetCartEntry,
  CatalogCartEntry,
  DynamicFilters,
  DynamicFiltersByProductType,
  DynamicFilterKey,
} from '@/types/storefront';
import { type Product } from '@/types/products';
import { type StorefrontAsset } from '@/types/assets';
import {
  INSTALLMENTS_CONFIG,
  ORDER_PROCESSING_DAYS,
  CURRENT_CATALOG_SHIPPING_METHODS,
  ZTP_HANDLING_DAYS,
  WAUTOPILOT_HANDLING_DAYS,
  WAREHOUSE_ORDER_PROCESSING_DAYS,
  CURRENT_WAREHOUSE_SHIPPING_METHODS,
} from '@/constants/storefront';
import { PaymentType } from '@/redux/slices/billingApiSlice';

export function isLaptop(product: Product | StorefrontAsset): boolean {
  return product.productType === ProductTypeName.Laptop;
}

/** Whether the product is an Apple device (laptop, desktop, tablet, phone) */
export const isAppleDevice = (product: Product) => {
  return (
    product.manufacturerName.toLowerCase() === 'apple' &&
    (product.productType === ProductTypeName.Laptop ||
      product.productType === ProductTypeName.Desktop ||
      product.productType === ProductTypeName.Tablets ||
      product.productType === ProductTypeName.CellPhone)
  );
};

/** Whether the product is a Windows device, eligible for ZTP (only laptops for now) */
export const isWindowsDeviceZtpEligible = (product: Product) => {
  return (
    product.manufacturerName.toLowerCase() !== 'apple' && // checking "non-Apple" for now (instead of checking Operating System)
    product.productType === ProductTypeName.Laptop // only laptops are eligible for ZTP now
  );
};

/** Whether the product is a Windows device, but not eligible for ZTP */
export const isWindowsDeviceNotZtpEligible = (product: Product) => {
  return (
    product.manufacturerName.toLowerCase() !== 'apple' && // checking "non-Apple" for now (instead of checking Operating System)
    (product.productType === ProductTypeName.Desktop ||
      product.productType === ProductTypeName.Tablets)
  );
};

/** Whether there is at least one Apple device (laptop, desktop, tablet, phone) in the Catalog cart */
export const hasAnyAppleDeviceEntry = (catalogCart: CatalogCartEntry[]) => {
  return catalogCart.some(entry => isAppleDevice(entry.item));
};

/** Whether there is at least one Windows device, eligible for ZTP (only laptops for now), in the Catalog cart */
export const hasAnyWindowsDeviceZtpEligibleEntry = (
  catalogCart: CatalogCartEntry[]
) => {
  return catalogCart.some(entry => isWindowsDeviceZtpEligible(entry.item));
};

/** Whether the product is eligible for Windows Autopilot (non-Apple laptop or desktop) */
export const isWindowsAutopilotEligible = (product: Product) => {
  return (
    product.manufacturerName.toLowerCase() !== 'apple' &&
    (product.productType === ProductTypeName.Laptop ||
      product.productType === ProductTypeName.Desktop)
  );
};

/** Whether there is at least one Windows Autopilot-eligible device in the Catalog cart */
export const hasAnyWindowsAutopilotEligibleEntry = (
  catalogCart: CatalogCartEntry[]
) => {
  return catalogCart.some(entry => isWindowsAutopilotEligible(entry.item));
};

/**
 * Merge dynamic filters from different product types into a single group of dynamic filters.
 * The merged object will combine filter keys from the respective product types and will merge the options of each filter key (from each product type).
 */
export const mergeDynamicFiltersFromProductTypes = (
  dynamicFiltersByProductType: DynamicFiltersByProductType
): DynamicFilters => {
  let mergedDynamicFilters: DynamicFilters = {};

  Object.values(dynamicFiltersByProductType).forEach(
    dynamicFiltersOfOneProductType => {
      Object.keys(dynamicFiltersOfOneProductType).forEach(filterKey => {
        // merging the options and using a Set to eliminate duplicates options
        const newDynamicFilterOptions = new Set([
          ...(mergedDynamicFilters[filterKey as DynamicFilterKey] ?? []),
          ...(dynamicFiltersOfOneProductType[filterKey as DynamicFilterKey] ??
            []),
        ]);

        mergedDynamicFilters = {
          ...mergedDynamicFilters,
          [filterKey]: Array.from(newDynamicFilterOptions), // converting the Set to array
        };
      });
    }
  );

  return mergedDynamicFilters;
};

export const isWarehouseAsset = (asset: AssetCartEntry) => {
  return asset.item.site?.organizationId === null;
};

export const assetCartContainsWarehouseAsset = (
  assetCart: AssetCartEntry[]
) => {
  return assetCart.some(isWarehouseAsset);
};

export const getAvailableInstallmentsCounts = () => {
  return Object.keys(INSTALLMENTS_CONFIG).map(Number);
};

export const getPaymentInterestPercentage = (installmentsCount: number) => {
  return INSTALLMENTS_CONFIG[installmentsCount]?.interestPercentage;
};

/**
 * Calculates the monthly installment value (based the number of installments and their corresponding interest rate).
 * No taxes are included.
 */
export const calculatePreTaxMonthlyInstallment = (
  amount: number,
  installmentsCount: number
) => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    throw new Error('Amount must be a valid number');
  }

  if (amount < 0) {
    throw new Error('Amount cannot be negative');
  }

  if (installmentsCount <= 0) {
    throw new Error('Number of installments cannot be zero or negative');
  }

  // Basically, it's the amount multiplied by the interest rate, then divided by the number of installments
  const monthlyInstallment =
    (amount * (1 + getPaymentInterestPercentage(installmentsCount) / 100)) /
    installmentsCount;

  return monthlyInstallment;
};

/**
 * Calculates the minimum pre-tax monthly installment value (considering the max number of installments)
 */
export const calculatePreTaxMinMonthlyInstallment = (amount: number) => {
  const maxInstallmentsCount = Math.max(...getAvailableInstallmentsCounts());

  return calculatePreTaxMonthlyInstallment(amount, maxInstallmentsCount);
};

/** Calculates the order totals, including taxes */
export const calculateOrderTotals = ({
  paymentType,
  installmentsCount,
  orderSubTotal,
  catalogShippingCost,
  warehouseShippingCost,
  totalTaxes,
  todayTax,
  futureMonthlyTax,
}: {
  paymentType: PaymentType;
  installmentsCount?: number;
  orderSubTotal: number;
  catalogShippingCost: number;
  warehouseShippingCost: number;
  totalTaxes: number;
  todayTax: number;
  futureMonthlyTax?: number;
}) => {
  if (paymentType === PaymentType.InFull) {
    // Order's total: the sum of everything (subTotal + shippings + totalTaxes)
    const orderTotal =
      orderSubTotal + catalogShippingCost + warehouseShippingCost + totalTaxes;

    return {
      orderTotal,
      todayTotal: orderTotal, // It's the order's total itself
      monthlyInstallment: 0, // No future payments
    };
  } else if (paymentType === PaymentType.InInstallments && installmentsCount) {
    const preTaxMonthlyInstallment = calculatePreTaxMonthlyInstallment(
      orderSubTotal,
      installmentsCount
    );

    // Monthly installment: pre-tax installment + their corresponding tax
    const monthlyInstallment =
      preTaxMonthlyInstallment + (futureMonthlyTax ?? 0);

    // Today's total: pre-tax installment + upfront fees (shippings) + their corresponding tax
    const todayTotal =
      preTaxMonthlyInstallment +
      catalogShippingCost +
      warehouseShippingCost +
      todayTax;

    // Order's total: today's total + remaining installments
    // The values were converted to fixed 2 decimal places before summing them up to get the real value that will be paid by the customer
    const orderTotal =
      parseFloat(todayTotal.toFixed(2)) +
      parseFloat(monthlyInstallment.toFixed(2)) * (installmentsCount - 1);

    return {
      orderTotal,
      todayTotal,
      monthlyInstallment,
    };
  }
};

/**
 * Calculates the number of months until a payment method expires.
 *
 * @param expirationMonth - The expiration month (1-12)
 * @param expirationYear - The expiration year (4 digits)
 * @returns The number of months until expiration (0 if already expired)
 */
export const calculateMonthsUntilExpiration = (
  expirationMonth: number,
  expirationYear: number
): number => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, we need 1-12

  // Calculate the difference in months
  const monthsDifference =
    (expirationYear - currentYear) * 12 + (expirationMonth - currentMonth);

  // Return 0 if already expired or expires this month, otherwise return the positive difference
  return Math.max(0, monthsDifference);
};

/** Get the ship date based on the order date and handling needs (e.g., provisioning time). */
export const getCatalogEstimatedShipDate = (
  orderDate: Date,
  ztpHandling: boolean,
  hasWAutopilotHandling: boolean
) => {
  let handlingDays = ORDER_PROCESSING_DAYS; // starts with default order processing time

  if (ztpHandling) {
    handlingDays = handlingDays + ZTP_HANDLING_DAYS; // Adds the ZTP handling days
  }

  if (hasWAutopilotHandling) {
    handlingDays = handlingDays + WAUTOPILOT_HANDLING_DAYS;
  }

  return addDays(orderDate, handlingDays);
};

/** Get the delivery date based on the shipping date, shopping country and catalog shipping method.
 *  Shipping date is when the package is handled to the carrier.
 */
export const getCatalogEstimatedDeliveryDate = (
  shippingDate: Date,
  shoppingCountryCode: Country,
  catalogShippingMethod: ShippingMethod
) => {
  return addDays(
    shippingDate,
    CURRENT_CATALOG_SHIPPING_METHODS[shoppingCountryCode]?.find(
      option => option.method === catalogShippingMethod
    )?.deliveryDays ?? 0
  );
};

/** Get the ship date for a warehouse order based on the order date. */
export const getWarehouseEstimatedShipDate = (orderDate: Date) => {
  return addDays(orderDate, WAREHOUSE_ORDER_PROCESSING_DAYS);
};

/** Get the delivery date based on the shipping date, shopping country and warehouse shipping method.
 *  Shipping date is when the package is handled to the carrier.
 */
export const getWarehouseEstimatedDeliveryDate = (
  shippingDate: Date,
  shoppingCountryCode: Country,
  warehouseShippingMethod: WarehouseShippingMethod
) => {
  return addDays(
    shippingDate,
    CURRENT_WAREHOUSE_SHIPPING_METHODS[shoppingCountryCode]?.find(
      option => option.method === warehouseShippingMethod
    )?.deliveryDays ?? 0
  );
};

/** Get related products (catalog products) for a given item (Asset or Catalog Product) and category.
 *  Only active products are returned, since inactive/discontinued products aren't purchasable
 *  and shouldn't be suggested to customers in the related accessories flyout or cart section.
 */
export const getItemRelatedProducts = (
  item: Product | StorefrontAsset,
  categoryName: RelatedProductCategoryNameEnum
) => {
  const products =
    item.relatedProductsByCategory?.find(
      category => category.categoryName === categoryName
    )?.products ?? [];

  return products.filter(
    product => product.status === ProductStatusEnum.Active
  );
};
